// Moved from `src/hooks/mutation/useMutateOrganizationById.ts` (now the app
// delegate) with the OT4 mutation slice (ARCFE-1033).
//
// WHY THE SESSION INVALIDATION IS A PORT AND NOT AN IMPORT (measured, ARCFE-1033)
// -----------------------------------------------------------------------------
// The legacy `onSettled` invalidated `SESSION_PERMISSIONS_QUERY_KEY`. That key
// is now single-site in `@heidi/session/contracts`, so importing it here looks
// legal — delivery section 7 lets a foreign domain contribute inert
// `/contracts` inside `/data`. It is not legal HERE, and the difference is that
// `workspace` is the FOUNDATIONAL domain.
//
// Measured by probe against `pnpm migration:check` rather than argued:
//
//   ERROR [domain-edges] undeclared cross-domain value import workspace -> session
//   ERROR [foundational] foundational domain "workspace" imports product domain(s) session
//
// A key constant is a VALUE import, so `census.ts` counts it as a real edge
// (only `typeOnly` specifiers are skipped) — the same reasoning that made
// `calls -> tasks` a declared edge. Declaring `workspace: ['session']` to
// satisfy it does not help; it adds two more hard errors:
//
//   ERROR [foundational] foundational domain "workspace" depends on product domain(s) session
//   ERROR [domain-edges] effective graph (explicit + foundational) has a cycle: session -> workspace -> session
//
// Every product domain implicitly depends on workspace, so workspace can never
// depend on one back. This is therefore the delivery section 3 command rule,
// not a contracts question: session owns the cache identity, and APP INTEGRATION
// sequences the invalidation at the delegate — exactly as the settled ruling
// already requires for the foreign COMMERCE invalidations elsewhere in this
// slice.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  GET_JOINED_ORGANIZATION,
  GET_ORGANIZATION_BY_ORGANIZATION_ID,
  type UpdateOrganizationRequest,
} from '../../contracts';
import { updateOrganizationById } from '../api/organization';

export const UPDATE_ORGANIZATION_BY_ID = 'UPDATE_ORGANIZATION_BY_ID';

export interface UpdateOrganizationByIdVariables {
  organizationId: string;
  updateOrganizationRequest: UpdateOrganizationRequest;
}

export interface UseMutateUpdateOrganizationByIdOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onCurrentUserRefreshNeeded?: () => void;
  /** App-reaction port: fired where the legacy success toast fired. */
  onOrganizationUpdated?: () => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onOrganizationUpdateError?: () => void;
  /**
   * App-reaction port fired in `onSettled` when the request changed
   * `allow_direct_session_sharing`, in the exact position the SESSION
   * permissions invalidation held — BEFORE the joined-organization
   * invalidation. The delegate owns that foreign command (see the header).
   */
  onDirectSessionSharingChanged?: () => void;
}

/** PATCH /organization/{organization_id} */
export function useMutateUpdateOrganizationById(
  options?: UseMutateUpdateOrganizationByIdOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [UPDATE_ORGANIZATION_BY_ID],
    mutationFn: async (input: UpdateOrganizationByIdVariables) => {
      return await updateOrganizationById(
        input.organizationId,
        input.updateOrganizationRequest
      );
    },
    onSuccess: () => {
      options?.onCurrentUserRefreshNeeded?.();
      options?.onOrganizationUpdated?.();
    },
    onError: () => {
      options?.onOrganizationUpdateError?.();
    },
    onSettled: (_, __, input) => {
      void queryClient.invalidateQueries({
        queryKey: [
          GET_ORGANIZATION_BY_ORGANIZATION_ID,
          { organizationId: input?.organizationId },
        ],
      });
      if ('allow_direct_session_sharing' in (input?.updateOrganizationRequest ?? {})) {
        options?.onDirectSessionSharingChanged?.();
        void queryClient.invalidateQueries({
          queryKey: [GET_JOINED_ORGANIZATION],
        });
      }
    },
  });
}
